import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UseFormReturn } from 'react-hook-form';

// Helper: Wait for specified milliseconds
const waitMs = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper: Type text slowly into an input with form.setValue sync
const typeSlowly = async (
  text: string, 
  inputElement: HTMLInputElement, 
  msPerChar: number,
  form?: UseFormReturn<any>,
  formFieldName?: string
) => {
  inputElement.focus({ preventScroll: true });
  let typedText = '';
  
  for (const char of text) {
    typedText += char;
    inputElement.value = typedText;
    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    
    // Sync with React Hook Form if provided
    if (form && formFieldName) {
      form.setValue(formFieldName, typedText, { shouldValidate: false });
    }
    
    await waitMs(msPerChar);
  }
};

// Helper: Select a Radix dropdown option with form.setValue sync
const selectDropdownOption = async (
  triggerTestId: string, 
  value: string,
  form?: UseFormReturn<any>,
  formFieldName?: string,
  setStateCallback?: (value: any) => void
) => {
  // Click the trigger to open dropdown
  const trigger = document.querySelector(`[data-testid="${triggerTestId}"]`) as HTMLElement | null;
  if (!trigger) return false;
  
  trigger.click();
  await waitMs(250); // Wait for dropdown animation to finish
  
  // Find and click the option
  const options = document.querySelectorAll('[role="option"]');
  for (const option of options) {
    if (option.textContent?.trim() === value) {
      (option as HTMLElement).click();
      
      // Sync with React Hook Form if provided
      if (form && formFieldName) {
        const numericValue = formFieldName.includes('year') ? parseInt(value) : value;
        form.setValue(formFieldName, numericValue, { shouldValidate: true });
      }
      
      // Call state setter if provided (for selectedYear, selectedMake, etc.)
      if (setStateCallback) {
        const numericValue = formFieldName?.includes('year') ? parseInt(value) : value;
        setStateCallback(numericValue);
      }
      
      await waitMs(150);
      return true;
    }
  }
  
  return false;
};

// Helper: Continuous smooth scroll from top to bottom over specified duration
const continuousScroll = async (durationMs: number) => {
  // Start from top of page
  window.scrollTo(0, 0);
  const startTime = performance.now();
  
  return new Promise<void>((resolve) => {
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      
      // Calculate max scroll (document height minus viewport)
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const targetScrollTop = maxScroll * progress;
      
      window.scrollTo({ top: targetScrollTop, behavior: 'instant' });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };
    
    requestAnimationFrame(animate);
  });
};

export const useDemoAutofill = (
  form: UseFormReturn<any>,
  setSelectedYear: (year: number | null) => void,
  setSelectedMake: (make: string | null) => void,
  setSelectedModel: (model: string | null) => void,
  setJobSearchQuery: (query: string) => void,
  setCustomHoursValue: (value: string) => void,
  setCustomHoursTouched: (touched: boolean) => void
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasRun = useRef(false);

  useEffect(() => {
    const runDemo = searchParams.get('demo') === 'true';
    
    if (!runDemo || hasRun.current) return;
    hasRun.current = true;

    // Run the cinematic demo sequence
    const runDemoSequence = async () => {
      // Step 1: Wait 750ms for page to fully render
      await waitMs(750);

      // NOTE: Job details box is force-rendered in demo mode via hardcoded demoJob constant
      // in CreateTicket.tsx - no state manipulation needed. Box appears instantly on page load.

      // Start continuous 10.5-second scroll from top to bottom (runs in background)
      continuousScroll(10500);

      // Step 2: Type customer name slowly (100ms per character) + form.setValue
      const nameInput = document.querySelector('[data-testid="customer-name-input"]') as HTMLInputElement;
      if (nameInput) {
        await typeSlowly("John Testerson", nameInput, 100, form, 'customerName');
      }

      // Step 3: Pause 750ms, then select Year 2020 + form.setValue
      await waitMs(750);
      await selectDropdownOption('vehicle-year-select', '2020', form, 'vehicle.year', setSelectedYear);

      // Step 4: Wait 400ms (for makes API), then select Toyota + form.setValue
      await waitMs(400);
      // Wait extra time for makes to load from API
      await waitMs(800);
      await selectDropdownOption('vehicle-make-select', 'Toyota', form, 'vehicle.make', setSelectedMake);

      // Step 5: Wait 400ms (for models API), then select Camry + form.setValue
      await waitMs(400);
      // Wait extra time for models to load from API
      await waitMs(800);
      await selectDropdownOption('vehicle-model-select', 'Camry', form, 'vehicle.model', setSelectedModel);

      // Step 6: Wait 600ms, then search for "Brake Inspection"
      await waitMs(600);
      const searchInput = document.querySelector('[data-testid="job-search-input"]') as HTMLInputElement;
      if (searchInput) {
        setJobSearchQuery(''); // Clear first
        
        await typeSlowly("Brake Inspection", searchInput, 80);
        setJobSearchQuery("Brake Inspection"); // Sync state
        
        await waitMs(400); // Wait for search results to appear
        
        // Click first search result
        const firstResult = document.querySelector('[cmdk-item]') as HTMLElement;
        if (firstResult) {
          firstResult.click();
          
          await waitMs(500);
          
          // Type custom hours ".4" - use state setter directly for controlled input
          let typedHours = '';
          for (const char of ".4") {
            typedHours += char;
            setCustomHoursValue(typedHours);
            await waitMs(120);
          }
          setCustomHoursTouched(true);
          
          await waitMs(300);
          
          // Type notes "Brakes grinding"
          const notesInput = document.getElementById('temp-notes') as HTMLInputElement;
          if (notesInput) {
            notesInput.focus({ preventScroll: true });
            notesInput.value = '';
            await waitMs(100);
            
            for (const char of "Brakes grinding") {
              notesInput.value += char;
              notesInput.dispatchEvent(new Event('input', { bubbles: true }));
              await waitMs(70);
            }
          }
          
          await waitMs(200);
          
          // Click Add Job button
          const addButton = document.querySelector('[data-testid="add-job-button"]') as HTMLElement;
          if (addButton) {
            addButton.click();
            
            // Wait for job to be added and page to expand
            await waitMs(400);
            
            // Scroll to show the newly added job
            window.scrollTo({ 
              top: document.documentElement.scrollHeight - window.innerHeight,
              behavior: 'smooth' 
            });
          }
        }
      }

      // Done - form is filled with smooth scroll to bottom showing added job
    };

    runDemoSequence();
  }, [searchParams, setSearchParams, form, setSelectedYear, setSelectedMake, setSelectedModel, setJobSearchQuery, setCustomHoursValue, setCustomHoursTouched]);
};
